import SharedArticleHeader from '@cx-web/components/page-template/ArticleHeader';
import { type MyOCData } from '@cx-web/domain-types/myoc';
import { useAuthModal, VIEW_LOGIN } from '@cx-web/feature-myoc-login/provider';
import { formatInTimeZone } from 'date-fns-tz';

import { type Repub, REPUBS } from '@cx-modules/constants';
import { type DeviceType } from '@cx-modules/types';

// RR7 atlas glue for the shared ARTICLE_HEADER_V1. The component (incl. the premium login wall,
// hero image, author details, and analytics tracking) lives once in
// `@cx-web/components/page-template/ArticleHeader` (shared with atlas-astro); this glue resolves
// the RR7 request context:
//   - `isLoggedIn` from myocData.myOCUser
//   - `imageWidth` from deviceType (MOBILE → 400px, desktop/tablet → 1000px)
//   - `publishedTimeLocalised` from repub timezone + locale via date-fns-tz
//   - `onLogin` from useAuthModal().openAtView(VIEW_LOGIN)
// The shared component imports no auth/runtime package, so it stays free of the dependency cycle.
export type ArticleHeaderProps = {
  aiGeneratedId?: string;
  authors: {
    fullName: string;
    id: string;
    image: { alt: string; url: string };
    shortName: string;
  }[];
  componentTags?: string[];
  // currentPathWithRepub is included here because ComponentLoaderContext spreads it onto all
  // components; it is not forwarded to the shared component (which does not use it).
  currentPathWithRepub: string;
  description?: string;
  deviceType: DeviceType;
  image: { alt: string; url: string };
  myocData: MyOCData;
  publishedTime?: string;
  readTime?: string;
  repub: Repub;
  title: string;
};

export const ArticleHeader = ({
  aiGeneratedId,
  authors,
  componentTags,
  description,
  deviceType,
  image,
  myocData,
  publishedTime,
  readTime,
  repub,
  title,
}: ArticleHeaderProps) => {
  const { openAtView } = useAuthModal();

  const isLoggedIn = !!myocData.myOCUser;
  const isMobile = deviceType === 'MOBILE';
  const imageWidth = isMobile ? 400 : 1000;

  const publishedTimeLocalised =
    publishedTime &&
    formatInTimeZone(
      publishedTime,
      REPUBS[repub].dateProperties.timezone,
      REPUBS[repub].dateProperties.format.timeDateFull,
      { locale: REPUBS[repub].dateProperties.locale },
    );

  return (
    <SharedArticleHeader
      aiGeneratedId={aiGeneratedId}
      authors={authors}
      componentTags={componentTags}
      description={description}
      image={image}
      imageWidth={imageWidth}
      isLoggedIn={isLoggedIn}
      onLogin={() => openAtView(VIEW_LOGIN)}
      publishedTimeLocalised={publishedTimeLocalised || undefined}
      readTime={readTime}
      title={title}
    />
  );
};
